import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/Button';
import { Textarea } from '@/shared/components/ui/Textarea';
import { WizardSuccessProps } from '../types/wizard.types';
import {
  PraiseFormData,
  FeedbackFormData,
  WizardAnswers,
  Employee,
  CompanyValue,
  getPraiseReasonFlowAnswers,
  getFeedbackReasonFlowAnswers,
  FormattedStepAnswer,
} from '@momentum/types';
import { UserSelectorAvatar } from '@/shared/components/user-selector';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import {
  TeamCollaborationIllustration,
  FeedbackIllustration,
} from '@/shared/components/illustrations';
import { useGoals } from '@/shared/hooks';
import { Loader2, ArrowLeftRight } from 'lucide-react';
import { formatDate } from '@/shared/utils/date-utils';
import { EffectSelectionOptions } from '@momentum/types';

type EffectTranslation = {
  title: string;
  beforeSend?: (name: string, impacted: string) => string;
  afterSend?: (name: string, impacted: string) => string;
};

interface PraiseReviewStepProps extends Omit<WizardSuccessProps, 'onClose'> {
  answers: WizardAnswers<PraiseFormData>;
  employees: Employee[];
  onSendInteraction?: () => void;
  onSaveDraft?: () => void;
  isSubmitting?: boolean;
  interactionType: 'praise';
  companyValues: CompanyValue[];
  isFinished?: boolean;
  creatorId?: string;
}

interface FeedbackReviewStepProps extends Omit<WizardSuccessProps, 'onClose'> {
  answers: WizardAnswers<FeedbackFormData>;
  employees: Employee[];
  onSendInteraction?: () => void;
  onSaveDraft?: () => void;
  isSubmitting?: boolean;
  interactionType: 'feedback';
  companyValues: CompanyValue[];
  creatorId?: string;
}

type InteractionReviewStepProps =
  | PraiseReviewStepProps
  | FeedbackReviewStepProps;

export const InteractionReviewStep: React.FC<InteractionReviewStepProps> = ({
  answers,
  employees,
  onSendInteraction,
  onSaveDraft,
  isSubmitting = false,
  onReset,
  interactionType,
  companyValues,
  creatorId,
}) => {
  const { t, i18n } = useTranslation('wizard');
  const employee = useEmployeeContext();
  const [description, setDescription] = useState(
    answers['interaction-description'] || ''
  );

  const isReceiver = creatorId && employee._id !== creatorId;
  // Find the selected employee
  const selectedEmployee = employees?.find(
    (employee: Employee) => employee._id === answers['employee-selection']?._id
  );

  const creator = employees?.find(
    (employee: Employee) => employee._id === creatorId
  );

  const { data: goals, isLoading: isLoadingGoals } = useGoals(employee?._id);

  // Format additional effects using i18n with beforeSend text
  const formatAdditionalEffectsDetailed = (effects: string[]) => {
    const effectsOptionsKey =
      interactionType === 'praise'
        ? 'praiseQuestions.additionalEffects.options'
        : 'feedbackQuestions.additionalEffects.options';

    const effectsOptions = t(effectsOptionsKey, {
      returnObjects: true,
    }) as Record<string, EffectTranslation>;

    const recipientName =
      selectedEmployee?.personalInfo?.firstName || 'this person';

    return effects
      .filter((effect) => effect !== 'no-other-effects')
      .map((effect) => {
        const effectOption = effectsOptions[effect];

        if (isReceiver && effectOption?.afterSend) {
          return effectOption.afterSend(
            creator?.personalInfo?.firstName || '',
            ''
          );
        }

        if (effectOption?.beforeSend) {
          return effectOption.beforeSend(recipientName, '');
        }
        return effectOption?.title || effect;
      });
  };

  // Format company values
  const formatCompanyValues = (
    companyValues: CompanyValue[],
    values: string[]
  ) => {
    return values
      .map(
        (value) =>
          companyValues.find((companyValue) => companyValue._id === value)
            ?.value
      )
      .join(', ');
  };

  // Get the appropriate translation keys based on interaction type
  const getTranslations = () => {
    const employeeName =
      (isReceiver
        ? creator?.personalInfo?.firstName
        : selectedEmployee?.personalInfo.firstName) || '';
    const recipientName = selectedEmployee?.personalInfo.firstName || '';
    const participantType = isReceiver ? 'receiver' : 'creator';
    const reviewKey =
      interactionType === 'praise'
        ? 'praiseQuestions.review'
        : 'feedbackQuestions.review';
    return {
      title: t(`${reviewKey}.title.${participantType}`, {
        employeeName: recipientName,
      }),
      subtitle: t(`${reviewKey}.subtitle.${participantType}`, {
        employeeName,
      }),
      description: t(`${reviewKey}.description`),
      date: t(`${reviewKey}.date`),
      reason: t(`${reviewKey}.reason`),
      reasonDetails: t(`${reviewKey}.reasonDetails`),
      additionalEffects: t(`${reviewKey}.additionalEffectsTitle`),
      additionalEffectsPrefix: t(`${reviewKey}.additionalEffectsPrefix`),
      companyValues: t(`${reviewKey}.companyValues`),
      edit: t(`${reviewKey}.edit`),
      saveDraft: t(`${reviewKey}.saveDraft`),
      sendInteraction: t(`${reviewKey}.send`),
      sending: t(`${reviewKey}.sending`),
      goalTitle: t(`${reviewKey}.goal.title.${participantType}`, {
        employeeName,
      }),
      goalPrefix: t(`${reviewKey}.goal.prefixBeforeSend.${participantType}`, {
        employeeName,
      }),
      goalAffection: t(`${reviewKey}.goal.affection`),
    };
  };

  const reasonDetails: FormattedStepAnswer[] = useMemo(() => {
    const formatContext = {
      selectedEmployee,
      formatDate: (date: Date | string) =>
        formatDate(date, 'dd MMM yyyy', i18n.language),
    };

    if (interactionType === 'feedback') {
      return getFeedbackReasonFlowAnswers(answers, t as any, formatContext);
    } else {
      return getPraiseReasonFlowAnswers(answers, t as any, formatContext);
    }
  }, [answers, interactionType, t, selectedEmployee, i18n.language]);

  const sendInteraction = () => {
    if (!onSendInteraction) return;

    answers['interaction-description'] = description;
    onSendInteraction();
  };

  const translations = getTranslations();

  return (
    <div className="max-w-xl xl:max-w-3xl mx-auto space-y-8 py-8 px-4">
      {/* Illustration based on interaction type */}
      <div className="flex justify-center">
        {interactionType === 'feedback' ? (
          <FeedbackIllustration />
        ) : (
          <TeamCollaborationIllustration />
        )}
      </div>

      <div className="text-center space-y-3">
        <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
          {translations.title}
        </h2>
        <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
          {translations.subtitle}
        </h2>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-8 lg:p-10 space-y-6">
        <div className="flex items-center justify-center gap-3 pb-4 border-b border-gray-100">
          <UserSelectorAvatar
            imageUrl={employee.personalInfo.imageUrl}
            fullName={
              employee.personalInfo.firstName +
              ' ' +
              employee.personalInfo.lastName
            }
            className="w-12 h-12"
          />

          <ArrowLeftRight className="w-5 h-5" />

          <UserSelectorAvatar
            imageUrl={selectedEmployee?.personalInfo.imageUrl}
            fullName={
              selectedEmployee?.personalInfo.firstName +
              ' ' +
              selectedEmployee?.personalInfo.lastName
            }
          />
        </div>

        {/* Date Selection */}
        {isReceiver && (
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
            <p className="text-sm font-medium text-gray-600">
              {translations.description}
            </p>
          </div>
        )}

        {/* Date Selection */}
        {answers['date-selection'] && (
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
            <p className="text-sm font-medium text-gray-600">
              {translations.date}
            </p>
            <p className="text-base font-semibold text-gray-900 mt-1">
              {formatDate(
                answers['date-selection'],
                'dd MMM yyyy',
                i18n.language
              )}
            </p>
          </div>
        )}

        {reasonDetails.length > 0 && (
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
            <p className="text-sm font-medium text-gray-600 mb-6">
              {translations.reasonDetails}
            </p>

            <div className="space-y-4">
              {reasonDetails.length > 0 && (
                <div
                  key={`${reasonDetails[0]?.stepId}-${0}`}
                  className="space-y-1"
                >
                  <p className="text-sm font-medium text-gray-500">
                    {reasonDetails[0]?.formattedQuestion}
                  </p>
                  <p className="text-base text-gray-900">
                    {reasonDetails[1]?.formattedAnswer}
                  </p>
                </div>
              )}
              {reasonDetails.slice(2).map((item, index) => (
                <div key={`${item.stepId}-${index}`} className="space-y-1">
                  <p className="text-sm font-medium text-gray-500">
                    {item.formattedQuestion}
                  </p>
                  <p className="text-base text-gray-900">
                    {item.formattedAnswer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {'goal-selection' in answers &&
          answers['goal-selection'] !== 'no-effect' && (
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
              <p className="text-sm font-medium text-gray-600">
                {translations.goalTitle}
              </p>
              {isLoadingGoals ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <p className="text-base text-gray-900 mt-1">
                  {translations.goalPrefix}{' '}
                  <span className="font-semibold">
                    {
                      goals?.find(
                        (goal) => goal._id === answers['goal-selection']
                      )?.title
                    }
                  </span>{' '}
                  {translations.goalAffection}{' '}
                  <span className="font-semibold">
                    {answers['goal-impact-range'] === 1
                      ? t('common.rangeDefaults.relatively')
                      : answers['goal-impact-range'] === 2
                      ? t('common.rangeDefaults.significantly')
                      : t('common.rangeDefaults.extremely')}
                  </span>
                </p>
              )}
            </div>
          )}

        {/* Additional Effects - different field names for praise vs feedback */}
        {'additional-effects' in answers &&
          answers['additional-effects'] &&
          answers['additional-effects']?.[0] !==
            EffectSelectionOptions.NO_OTHER_EFFECTS && (
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
              <p className="text-sm font-medium text-gray-600 mb-2">
                {translations.additionalEffects}
              </p>
              <div className="space-y-2">
                {formatAdditionalEffectsDetailed(
                  answers['additional-effects']
                ).map((effectText, index) => (
                  <p
                    key={index}
                    className="text-base font-semibold text-gray-900"
                  >
                    {effectText}
                  </p>
                ))}
              </div>
            </div>
          )}

        {/* Company Values */}
        {answers['company-values'] && (
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
            <p className="text-sm font-medium text-gray-600">
              {translations.companyValues}
            </p>
            <p className="text-base font-semibold text-gray-900 mt-1">
              {formatCompanyValues(companyValues, answers['company-values'])}
            </p>
          </div>
        )}

        {/* Description */}
        <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
          <p className="text-sm font-medium text-gray-600">
            {t('common.labels.interactionDescription')}
          </p>
          <Textarea
            className="mt-2"
            placeholder={t('common.placeholders.interactionDescription')}
            rows={4}
            onChange={(e) => setDescription(e.target.value)}
            value={description}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        {onReset && (
          <Button
            variant="outline"
            onClick={onReset}
            disabled={isSubmitting}
            className="px-6 py-2.5 rounded-xl border-gray-400 hover:bg-gray-100 font-medium"
          >
            {translations.edit}
          </Button>
        )}

        {onSaveDraft && (
          <Button
            variant="outline"
            onClick={onSaveDraft}
            disabled={isSubmitting}
            className="px-6 py-2.5 rounded-xl border-gray-400 hover:bg-gray-100 font-medium"
          >
            {translations.saveDraft}
          </Button>
        )}

        {onSendInteraction && (
          <Button
            onClick={sendInteraction}
            disabled={isSubmitting}
            className="px-8 py-2.5 bg-primary hover:bg-primary/90 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 font-medium"
          >
            {isSubmitting ? translations.sending : translations.sendInteraction}
          </Button>
        )}
      </div>
    </div>
  );
};
